/**
 * Collection Filter System
 * Handles AJAX filtering without page reloads for better UX
 */

// Debounce utility function
function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const later = function() {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    // Initialize active filter remove buttons
    this.initActiveFilters();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove, .active-filter-tag__remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    
    // Add loading state
    const productGrid = document.getElementById('CollectionProductGrid') || document.querySelector('.collection-grid');
    if (productGrid) {
      productGrid.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;
      
      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGrid(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((error) => {
        console.error('Filter fetch error:', error);
        // Fallback to page reload on error
        window.location.href = url.replace('&section_id=', '?section_id=').split('?section_id=')[0] + '?' + url.split('?')[1].replace(/section_id=[^&]*&?/, '');
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGrid(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGrid(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Try multiple selectors for product grid
    const selectors = ['#CollectionProductGrid', '.collection-grid', '.collection-products-inner'];
    let sourceGrid = null;
    let targetGrid = null;

    for (const selector of selectors) {
      sourceGrid = doc.querySelector(selector);
      targetGrid = document.querySelector(selector);
      if (sourceGrid && targetGrid) break;
    }

    if (sourceGrid && targetGrid) {
      targetGrid.innerHTML = sourceGrid.innerHTML;
      
      // Process any templates
      targetGrid.querySelectorAll('template').forEach(elm => {
        elm.closest('form')?.append(elm.content.cloneNode(true));
      });

      // Remove loading state
      targetGrid.classList.remove('loading');
      
      // Reinitialize any product card interactions
      if (window.initProductCards) {
        window.initProductCards();
      }
    }
  }

  static renderProductCount(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Try multiple selectors for product count
    const selectors = ['#CollectionProductCount', '.collection-count', '.collection-meta'];
    
    for (const selector of selectors) {
      const sourceCount = doc.querySelector(selector);
      if (sourceCount) {
        document.querySelectorAll(selector).forEach(elm => {
          elm.innerHTML = sourceCount.innerHTML;
        });
        break;
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    // Update filter form content
    const sourceFilters = parsedHTML.querySelector('.collection-filters__form');
    const targetFilters = document.querySelector('.collection-filters__form');
    
    if (sourceFilters && targetFilters) {
      // Preserve the current form action and method
      const currentAction = targetFilters.action;
      const currentMethod = targetFilters.method;
      
      targetFilters.innerHTML = sourceFilters.innerHTML;
      targetFilters.action = currentAction;
      targetFilters.method = currentMethod;
      
      // Reinitialize filter interactions
      const newForm = document.querySelector('facet-filters-form');
      if (newForm) {
        newForm.initActiveFilters();
        newForm.initPriceRanges();
      }
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderActiveFacets(html) {
    const activeFacetSelectors = ['.active-filters', '.active-facets-mobile', '.active-facets-desktop'];
    
    activeFacetSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      
      if (sourceElement && targetElement) {
        targetElement.innerHTML = sourceElement.innerHTML;
      }
    });
  }

  static updateURLHash(searchParams) {
    const newUrl = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`;
    history.pushState({ searchParams }, '', newUrl);
  }

  static getSections() {
    // Try to find the main collection section
    const mainSection = document.querySelector('[data-section-type="collection"]') || 
                       document.querySelector('.collection-page') ||
                       document.querySelector('main');
    
    return [
      {
        id: 'main-collection',
        section: mainSection ? mainSection.dataset.sectionId || 'collection' : 'collection',
      }
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();
    
    // Process form data - handle price filters properly
    formData.forEach((value, key) => {
      if (key.includes('price.gte') || key.includes('price.lte') || 
          key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
        // For native Shopify filters, try sending dollar values directly
        // Shopify might handle the cents conversion internally
        const priceValue = parseFloat(value);
        if (priceValue > 0) {
          console.log(`Price Filter Debug: ${key} = ${value} dollars (sending as-is)`);
          params.set(key, priceValue.toString());
        }
      } else {
        params.set(key, value);
      }
    });
    
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    const form = event.target.closest('form');
    if (!form) return;

    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    
    const url = event.currentTarget.href;
    const searchParams = url.indexOf('?') === -1 ? '' : url.slice(url.indexOf('?') + 1);
    
    FacetFiltersForm.renderPage(searchParams);
  }

  initActiveFilters() {
    // Initialize remove filter buttons
    this.querySelectorAll('.js-facet-remove').forEach(button => {
      button.addEventListener('click', this.onActiveFilterClick.bind(this));
    });
  }

  initPriceRanges() {
    // Initialize price range inputs (both old and new structure)
    this.querySelectorAll('.price-input, .field__input[type="number"]').forEach(input => {
      input.addEventListener('change', this.debouncedOnSubmit);
    });

    // Initialize price range sliders
    this.querySelectorAll('.price-range-slider').forEach(slider => {
      this.initPriceSlider(slider);
    });

    // Initialize price-range custom elements
    this.querySelectorAll('price-range').forEach(priceRange => {
      // Custom elements handle their own initialization
      // Just ensure they trigger form submission
      const inputs = priceRange.querySelectorAll('input[type="number"]');
      inputs.forEach(input => {
        input.addEventListener('change', this.debouncedOnSubmit);
      });
    });
  }

  initPriceSlider(sliderContainer) {
    const minSlider = sliderContainer.querySelector('.price-range-slider__input--min');
    const maxSlider = sliderContainer.querySelector('.price-range-slider__input--max');
    const minInput = sliderContainer.parentElement.querySelector('.price-input--min');
    const maxInput = sliderContainer.parentElement.querySelector('.price-input--max');
    const range = sliderContainer.querySelector('.price-range-slider__range');

    if (!minSlider || !maxSlider || !minInput || !maxInput || !range) return;

    const updateSlider = () => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);
      const minPrice = parseInt(minSlider.min);
      const maxPrice = parseInt(minSlider.max);

      if (minVal > maxVal - 1) {
        minSlider.value = maxVal - 1;
      }

      if (maxVal < minVal + 1) {
        maxSlider.value = minVal + 1;
      }

      const minPercent = ((minSlider.value - minPrice) / (maxPrice - minPrice)) * 100;
      const maxPercent = ((maxSlider.value - minPrice) / (maxPrice - minPrice)) * 100;

      range.style.left = minPercent + '%';
      range.style.width = (maxPercent - minPercent) + '%';

      minInput.value = minSlider.value;
      maxInput.value = maxSlider.value;
    };

    const updateFromInput = () => {
      const minVal = parseInt(minInput.value);
      const maxVal = parseInt(maxInput.value);

      if (minVal >= 0 && minVal <= parseInt(minSlider.max)) {
        minSlider.value = minVal;
      }

      if (maxVal >= 0 && maxVal <= parseInt(maxSlider.max)) {
        maxSlider.value = maxVal;
      }

      updateSlider();
    };

    minSlider.addEventListener('input', updateSlider);
    maxSlider.addEventListener('input', updateSlider);
    minInput.addEventListener('change', updateFromInput);
    maxInput.addEventListener('change', updateFromInput);

    // Initialize
    updateSlider();
  }
}

// Initialize static properties
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

// Define custom elements
customElements.define('facet-filters-form', FacetFiltersForm);

// Set up listeners when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    FacetFiltersForm.setListeners();
  });
} else {
  FacetFiltersForm.setListeners();
}

// Clear all filters function (global)
window.clearAllFilters = function() {
  const filterForm = document.querySelector('.collection-filters__form');
  if (filterForm) {
    window.location.href = filterForm.action;
  }
};

// Remove individual filter function (global)
window.removeFilter = function(filterType, filterValue) {
  const url = new URL(window.location);
  const params = new URLSearchParams(url.search);
  
  if (filterType === 'price') {
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
  } else if (filterType === 'vendor') {
    const currentVendors = params.get('filter.v.vendor');
    if (currentVendors) {
      const vendorArray = currentVendors.split(',');
      const filteredVendors = vendorArray.filter(v => v !== filterValue);
      if (filteredVendors.length > 0) {
        params.set('filter.v.vendor', filteredVendors.join(','));
      } else {
        params.delete('filter.v.vendor');
      }
    }
  } else if (filterType === 'type') {
    const currentTypes = params.get('filter.v.type');
    if (currentTypes) {
      const typeArray = currentTypes.split(',');
      const filteredTypes = typeArray.filter(t => t !== filterValue);
      if (filteredTypes.length > 0) {
        params.set('filter.v.type', filteredTypes.join(','));
      } else {
        params.delete('filter.v.type');
      }
    }
  } else if (filterType === 'availability') {
    const currentAvailability = params.get('filter.v.availability');
    if (currentAvailability) {
      const availabilityArray = currentAvailability.split(',');
      const filteredAvailability = availabilityArray.filter(a => a !== filterValue);
      if (filteredAvailability.length > 0) {
        params.set('filter.v.availability', filteredAvailability.join(','));
      } else {
        params.delete('filter.v.availability');
      }
    }
  }
  
  url.search = params.toString();
  window.location.href = url.toString();
};

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { FacetFiltersForm };
}
